import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import "./ContactUs.css";

const ContactUs: React.FC = () => {
  const [validated, setValidated] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
    } else {
      // Here you would normally send the form data to your backend
      alert("Thank you for your message! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    }

    setValidated(true);
  };

  const handleBookMeeting = () => {
    window.open("https://calendly.com/makda-fyled/30min", "_blank");
  };

  return (
    <div className="contact-us-page">
      {/* Hero Section */}
      <section className="contact-us-hero-section py-5 mb-5">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <div className="contact-us-hero-content">
                <h1 className="display-4 fw-bold text-white mb-4">
                  Need help with scientific IP? Get in touch!
                </h1>
                <p className="lead text-white mb-5">
                  Contact us to streamline your scientific IP workflow.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <Container>
        {/* Book a Meeting Section - Now at the top */}
        <Row className="mb-5">
          <Col xl={10} className="mx-auto">
            <Card className="contact-us-book-meeting-hero shadow-lg border-0">
              <Card.Body className="text-center py-5 px-4">
                <div className="contact-us-meeting-icon-wrapper mb-4">
                  <i className="bi bi-calendar-check-fill contact-us-meeting-icon text-primary"></i>
                </div>
                <h2 className="h2 fw-bold text-primary mb-3">
                  Ready to See Fyled AI in Action?
                </h2>
                <p className="lead text-muted mb-4 px-md-5">
                  Schedule a personalized demo or book a consultation to
                  discover how how our AI-powered platform could support your
                  intellectual property (IP) strategy.
                </p>
                <div className="contact-us-cta-buttons">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleBookMeeting}
                    className="contact-us-btn-primary me-3 mb-3 mb-md-0 px-4 py-3 fw-semibold"
                  >
                    <i className="bi bi-calendar-plus me-2"></i>
                    Schedule a Demo
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="lg"
                    onClick={handleBookMeeting}
                    className="contact-us-btn-outline-primary px-4 py-3 fw-semibold"
                  >
                    <i className="bi bi-chat-dots me-2"></i>
                    Book Consultation
                  </Button>
                </div>
                <p className="text-muted mt-4 mb-0">
                  <i className="bi bi-clock me-2 text-primary"></i>
                  <small>
                    30-minute personalized session • No commitment required
                  </small>
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Contact Form and Info Section */}
        <Row className="g-5 mb-5">
          {/* Contact Form */}
          <Col lg={8}>
            <Card className="contact-us-form-card shadow-lg mt-1 border-0 h-80">
              <Card.Header className="contact-us-form-header text-center py-4 border-0">
                <h2 className="h3 mb-2 text-white fw-bold">
                  Send Us a Message
                </h2>
                <p className="mb-0 text-white">
                  Fill out the form below and we'll get back to you within 24
                  hours
                </p>
              </Card.Header>
              <Card.Body className="p-4 p-md-5">
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Group controlId="formName">
                        <Form.Label className="fw-semibold text-dark">
                          Full Name *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          className="contact-us-form-input"
                          size="sm"
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide your name.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formEmail">
                        <Form.Label className="fw-semibold text-dark">
                          Email Address *
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email address"
                          className="contact-us-form-input"
                          size="sm"
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid email address.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="formSubject">
                    <Form.Label className="fw-semibold text-dark">
                      Subject *
                    </Form.Label>
                    <Form.Select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="contact-us-form-input"
                      size="sm"
                      required
                    >
                      <option value="">What can we help you with?</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Product Demo">Product Demo Request</option>
                      <option value="Technical Support">
                        Technical Support
                      </option>
                      <option value="Partnership">
                        Partnership Opportunity
                      </option>
                      <option value="Other">Other</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Please select a subject.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="formMessage">
                    <Form.Label className="fw-semibold text-dark">
                      Message *
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell us more about your needs and how we can help..."
                      className="contact-us-form-input"
                      style={{ height: "75px" }}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a message.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      variant="primary"
                      size="sm"
                      type="submit"
                      className="py-3 fw-semibold contact-us-submit-btn"
                    >
                      <i className="bi bi-send me-2"></i>
                      Send Message
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Contact Information */}
          <Col lg={4}>
            <div className="contact-us-info-section h-100">
              <h3 className="h4 fw-bold text-dark mb-4">Contact Information</h3>

              <Card className="contact-us-info-card shadow-sm border-0 mb-4">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-start">
                    <div className="contact-us-icon-wrapper me-3">
                      <i className="bi bi-geo-alt-fill text-primary"></i>
                    </div>
                    <div>
                      <h5 className="fw-semibold mb-2">Our Office</h5>
                      <p className="text-muted mb-0">
                        9110 Alcosta Blvd, Ste H #3021
                        <br />
                        San Ramon, CA 94583
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="contact-us-info-card shadow-sm border-0 mb-4">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-start">
                    <div className="contact-us-icon-wrapper me-3">
                      <i className="bi bi-envelope-fill text-primary"></i>
                    </div>
                    <div>
                      <h5 className="fw-semibold mb-2">Email Us</h5>
                      <p className="mb-0">
                        <a
                          href="mailto:info@fyled.ai"
                          className="text-primary text-decoration-none"
                        >
                          info@fyled.ai
                        </a>
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="contact-us-response-time-card shadow-sm border-0">
                <Card.Body className="p-4 text-center">
                  <i className="bi bi-clock-fill text-primary fs-2 mb-3"></i>
                  <h5 className="fw-semibold mb-2">Quick Response</h5>
                  <p className="text-muted mb-0">
                    We typically respond within
                    <br />
                    <strong className="text-primary">24 hours</strong>
                  </p>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>

        {/* FAQ Teaser Section */}
        {/* <Row className="mt-5 pt-5">
          <Col lg={8} className="mx-auto text-center">
            <div className="contact-us-faq-teaser">
              <h3 className="h4 fw-bold text-dark mb-3">
                Have Common Questions?
              </h3>
              <p className="text-muted mb-4">
                Check out our FAQ section for quick answers to the most
                frequently asked questions about Fyled AI.
              </p>
              <Button variant="outline-primary" size="sm" className="px-4">
                <i className="bi bi-question-circle me-2"></i>
                View FAQ
              </Button>
            </div>
          </Col>
        </Row> */}
      </Container>
    </div>
  );
};

export default ContactUs;
